import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
  Filter,
  Grid3X3,
  List,
  Loader2,
  Calendar as CalendarIcon,
  Play,
  Users,
  Sparkles,
  Hash,
  CheckCircle
} from 'lucide-react';
import PostCard from '../components/PostCard';
import EditModal from '../components/EditModal';
import CalendarSummary from '../components/CalendarSummary';
import { plannerAPI, exportAPI } from '../api';
import { useCalendarStore } from '../store/calendarStore';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function CalendarView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedItem, setSelectedItem, clearSelectedItem } = useCalendarStore();

  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [filterPersona, setFilterPersona] = useState('all');
  const [filterSubreddit, setFilterSubreddit] = useState('all');
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStage, setProgressStage] = useState(0);
  const [completedCalendar, setCompletedCalendar] = useState(null);

  // Fetch calendar
  const { data: calendar, isLoading, error } = useQuery({
    queryKey: ['calendar', id],
    queryFn: () => plannerAPI.getCalendar(id),
    select: (res) => res.data,
    enabled: !!id
  });

  // Regenerate mutation
  const regenerateMutation = useMutation({
    mutationFn: (itemId) => plannerAPI.regenerateItem(id, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries(['calendar', id]);
      toast.success('Content regenerated');
    },
    onError: () => {
      toast.error('Failed to regenerate');
    }
  });

  // Save/Update mutation
  const saveMutation = useMutation({
    mutationFn: ({ itemId, updates }) => plannerAPI.updateItem(id, itemId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries(['calendar', id]);
      toast.success('Changes saved successfully!');
      clearSelectedItem();
    },
    onError: () => {
      toast.error('Failed to save changes');
    }
  });

  // Next week mutation
  const nextWeekMutation = useMutation({
    mutationFn: () => plannerAPI.deriveNextWeek(id),
    onSuccess: (res) => {
      const newCalendar = res.data;
      setCompletedCalendar(newCalendar);
      setProgressStage(5);
    },
    onError: () => {
      setShowProgressModal(false);
      toast.error('Failed to generate next week');
    }
  });

  // Progress stages simulation for next week generation
  useEffect(() => {
    if (showProgressModal && nextWeekMutation.isPending) {
      const stages = [1, 2, 3, 4];
      let currentIndex = 0;
      
      const interval = setInterval(() => {
        if (currentIndex < stages.length) {
          setProgressStage(stages[currentIndex]);
          currentIndex++;
        }
      }, 2000);
      
      return () => clearInterval(interval);
    }
  }, [showProgressModal, nextWeekMutation.isPending]);

  const handleNextWeek = () => {
    setShowProgressModal(true);
    setProgressStage(0);
    setCompletedCalendar(null);
    nextWeekMutation.mutate();
  };

  const handleViewNewCalendar = () => {
    if (completedCalendar) {
      setShowProgressModal(false);
      // Invalidate current calendar to update the nextCalendarId link
      queryClient.invalidateQueries(['calendar', id]);
      navigate(`/calendar/${completedCalendar.id}`);
    }
  };

  const progressStages = [
    { id: 0, label: 'Initializing...', icon: Loader2 },
    { id: 1, label: 'Analyzing personas & subreddits', icon: Users },
    { id: 2, label: 'Generating content angles', icon: Sparkles },
    { id: 3, label: 'Creating AI-powered posts', icon: Hash },
    { id: 4, label: 'Finalizing calendar', icon: CalendarIcon },
    { id: 5, label: 'Complete!', icon: CheckCircle }
  ];

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: (format) => exportAPI.exportCalendar(id, format),
    onSuccess: (res, format) => {
      const blob = new Blob([res.data], { type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Use company name and date for better filename
      const companyName = (calendar?.input?.company || 'Calendar').replace(/[^a-zA-Z0-9\s-_]/g, '').replace(/\s+/g, '_').substring(0, 50);
      const dateStr = new Date().toISOString().split('T')[0];
      a.download = `${companyName}_Content_Calendar_${dateStr}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    }
  });

  // Filter and organize posts
  const organizedPosts = useMemo(() => {
    if (!calendar?.items) return {};

    const items = calendar.items.filter(item => {
      if (filterPersona !== 'all' && item.persona_id !== filterPersona) return false;
      if (filterSubreddit !== 'all' && item.subreddit !== filterSubreddit) return false;
      return true;
    });

    // Group by day
    const byDay = {};
    DAYS.forEach(day => {
      byDay[day] = items.filter(item => item.day === day);
    });
    return byDay;
  }, [calendar, filterPersona, filterSubreddit]);

  // Unique filters
  const personas = useMemo(() => {
    if (!calendar?.items) return [];
    const unique = [...new Set(calendar.items.map(i => i.persona_id))];
    return unique;
  }, [calendar]);

  const subreddits = useMemo(() => {
    if (!calendar?.items) return [];
    const unique = [...new Set(calendar.items.map(i => i.subreddit))];
    return unique;
  }, [calendar]);

  const handleEditItem = (item) => {
    setSelectedItem(item);
  };

  const handleSaveEdit = (updates) => {
    if (selectedItem) {
      saveMutation.mutate({ 
        itemId: selectedItem.id, 
        updates 
      });
    }
  };

  const handleRegenerateFromModal = () => {
    if (selectedItem) {
      clearSelectedItem();
      regenerateMutation.mutate(selectedItem.id);
    }
  };

  const handleSimulateItem = (item) => {
    navigate(`/simulator?itemId=${item.id}&calendarId=${id}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-reddit-orange" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 mb-4">Failed to load calendar</p>
        <button 
          onClick={() => navigate('/')} 
          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 font-medium rounded-full hover:bg-gray-300 transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {calendar?.input?.company?.name || 'Content Calendar'}
            </h1>
            <p className="text-sm text-gray-500">
              Week of {calendar?.weekStart || 'N/A'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => exportMutation.mutate('csv')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded-full hover:bg-gray-300 transition-colors"
            disabled={exportMutation.isPending}
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            <span>CSV</span>
          </button>
          <button
            onClick={() => exportMutation.mutate('xlsx')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 text-gray-800 text-sm font-medium rounded-full hover:bg-gray-300 transition-colors"
            disabled={exportMutation.isPending}
          >
            <Download className="w-4 h-4 flex-shrink-0" />
            <span>Excel</span>
          </button>
          <button
            onClick={handleNextWeek}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-reddit-orange text-white text-sm font-medium rounded-full hover:bg-reddit-orangeLight disabled:opacity-50 transition-colors"
            disabled={nextWeekMutation.isPending}
          >
            {nextWeekMutation.isPending ? (
              <Loader2 className="w-4 h-4 flex-shrink-0 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 flex-shrink-0" />
            )}
            <span>Next Week</span>
          </button>
        </div>
      </div>

      {/* Summary */}
      <CalendarSummary summary={calendar?.summary} />

      {/* Filters & View Toggle */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white rounded-lg border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <select
              value={filterPersona}
              onChange={(e) => setFilterPersona(e.target.value)}
              className="input py-1.5 pr-8 text-sm"
            >
              <option value="all">All Personas</option>
              {personas.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>

          <select
            value={filterSubreddit}
            onChange={(e) => setFilterSubreddit(e.target.value)}
            className="input py-1.5 pr-8 text-sm"
          >
            <option value="all">All Subreddits</option>
            {subreddits.map(s => (
              <option key={s} value={s}>r/{s}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 self-start sm:self-auto">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
          >
            <Grid3X3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Week Navigation - Navigate between linked weeks */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => calendar?.previousCalendarId && navigate(`/calendar/${calendar.previousCalendarId}`)}
          className={`p-2 rounded-lg ${!calendar?.previousCalendarId ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          disabled={!calendar?.previousCalendarId}
          title={calendar?.previousCalendarId ? 'Go to previous week' : 'No previous week'}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 text-gray-700">
          <CalendarIcon className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">Week {calendar?.weekNumber || 1}</span>
        </div>
        <button
          onClick={() => calendar?.nextCalendarId && navigate(`/calendar/${calendar.nextCalendarId}`)}
          className={`p-2 rounded-lg ${!calendar?.nextCalendarId ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-gray-100'}`}
          disabled={!calendar?.nextCalendarId}
          title={calendar?.nextCalendarId ? 'Go to next week' : "Use 'Next Week' button to generate"}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Calendar Grid View */}
      {viewMode === 'grid' && (
        <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
          <div className="grid grid-cols-7 gap-1 sm:gap-2 min-w-[700px]">
            {DAYS.map(day => (
              <div key={day} className="min-h-[200px] sm:min-h-[300px]">
                <div className="bg-gray-100 rounded-t-lg p-1.5 sm:p-2 text-center">
                  <span className="font-medium text-gray-700 text-xs sm:text-sm">{day.slice(0, 3)}</span>
                  <span className="ml-1 sm:ml-2 text-xs text-gray-500">
                    ({organizedPosts[day]?.length || 0})
                  </span>
                </div>
                <div className="bg-white border border-gray-200 border-t-0 rounded-b-lg p-1.5 sm:p-2 space-y-1.5 sm:space-y-2 min-h-[160px] sm:min-h-[260px]">
                  {organizedPosts[day]?.map(item => (
                    <div
                      key={item.id}
                      className="p-1.5 sm:p-2 bg-gray-50 rounded border border-gray-100 cursor-pointer hover:border-reddit-orange transition-colors"
                      onClick={() => handleEditItem(item)}
                    >
                      <div className="flex items-center justify-between mb-1 gap-1">
                        <span className="text-xs font-medium text-reddit-orange truncate">
                          r/{item.subreddit?.replace('r/', '')}
                        </span>
                        <span className={`text-xs px-1 sm:px-1.5 py-0.5 rounded flex-shrink-0 ${
                          item.score >= 8 ? 'bg-green-100 text-green-700' :
                          item.score >= 6 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {typeof item.score === 'number' ? item.score.toFixed(1) : '--'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-700 line-clamp-2">
                        {item.title || item.content?.substring(0, 50)}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">{item.time}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSimulateItem(item);
                          }}
                          className="flex items-center gap-1 px-2 py-1 bg-reddit-orange/10 text-reddit-orange text-xs font-medium rounded hover:bg-reddit-orange hover:text-white transition-colors"
                          title="Simulate Thread"
                        >
                          <Play className="w-3 h-3" />
                          <span className="hidden sm:inline">Simulate</span>
                        </button>
                      </div>
                    </div>
                  ))}
                  {(!organizedPosts[day] || organizedPosts[day].length === 0) && (
                    <div className="text-center text-gray-400 text-xs py-4 sm:py-8">
                      No posts
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-4">
          {DAYS.map(day => (
            <div key={day}>
              <h3 className="font-medium text-gray-700 mb-2">
                {day}
                <span className="ml-2 text-sm text-gray-400">
                  ({organizedPosts[day]?.length || 0} posts)
                </span>
              </h3>
              <div className="space-y-2">
                {organizedPosts[day]?.map(item => (
                  <PostCard
                    key={item.id}
                    item={item}
                    onEdit={() => handleEditItem(item)}
                    onSimulate={() => handleSimulateItem(item)}
                  />
                ))}
                {(!organizedPosts[day] || organizedPosts[day].length === 0) && (
                  <p className="text-gray-400 text-sm py-2">No posts scheduled</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {selectedItem && (
        <EditModal
          item={selectedItem}
          onClose={clearSelectedItem}
          onSave={handleSaveEdit}
          isSaving={saveMutation.isPending}
        />
      )}

      {/* Progress Modal for Next Week Generation */}
      {showProgressModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 sm:p-8 animate-zoom-in">
            {/* Header */}
            <div className="text-center mb-6">
              {progressStage < 5 ? (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-reddit-orange to-orange-500 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Generating Next Week</h2>
                  <p className="text-gray-500 text-sm mt-1">This may take a minute...</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center animate-zoom-in">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Week {(calendar?.weekNumber || 1) + 1} Ready!</h2>
                  <p className="text-gray-500 text-sm mt-1">Your next week's calendar has been generated</p>
                </>
              )}
            </div>

            {/* Progress Steps */}
            <div className="space-y-3 mb-6">
              {progressStages.slice(1).map((stage) => {
                const StageIcon = stage.icon;
                const isComplete = progressStage > stage.id || (stage.id === 5 && progressStage === 5);
                const isActive = progressStage === stage.id && stage.id !== 5;
                const isPending = progressStage < stage.id;

                return (
                  <div
                    key={stage.id}
                    className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                      isActive ? 'bg-orange-50 border border-reddit-orange' :
                      isComplete ? 'bg-green-50 border border-green-200' :
                      'bg-gray-50 border border-gray-100'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-reddit-orange text-white' :
                      isComplete ? 'bg-green-500 text-white' :
                      'bg-gray-200 text-gray-400'
                    }`}>
                      {isComplete ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : isActive ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <StageIcon className="w-4 h-4" />
                      )}
                    </div>
                    <span className={`text-sm font-medium ${
                      isActive ? 'text-reddit-orange' :
                      isComplete ? 'text-green-700' :
                      'text-gray-400'
                    }`}>
                      {stage.label}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Summary when complete */}
            {progressStage === 5 && completedCalendar && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {completedCalendar.summary?.total_posts || completedCalendar.items?.length || 0}
                    </div>
                    <div className="text-xs text-gray-500">Total Posts</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">
                      {completedCalendar.summary?.avg_score?.toFixed(1) || '--'}
                    </div>
                    <div className="text-xs text-gray-500">Avg Score</div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Button */}
            {progressStage === 5 && (
              <button
                onClick={handleViewNewCalendar}
                className="w-full py-3 bg-reddit-orange text-white font-semibold rounded-lg hover:bg-reddit-orangeLight transition-colors"
              >
                View Week {(calendar?.weekNumber || 1) + 1} Calendar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
